import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Grid,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Divider,
  Badge,
  Text,
} from '@chakra-ui/react';
import api from '../../services/api';
import BasicInfoForm from './forms/BasicInfoForm';
import EventForm from './forms/EventForm';
import VenueForm from './forms/VenueForm';
import BudgetForm from './forms/BudgetForm';
import PreferencesForm from './forms/PreferencesForm';
import CommentsForm from './forms/CommentsForm';
import TasksForm from './forms/TasksForm';
import CallsForm from './forms/CallsForm';
import ProposalForm from './forms/ProposalForm';

const initialFormData = {
  phone: '',
  email: '',
  pointOfContact: '',
  brideAndGroomName: '',
  stage: 'stage_lead_in',
  priority: 'medium',
  events: [{
    type: '',
    guestCount: null,
    startTime: '',
    endTime: '',
    notes: ''
  }],
  eventDates: [],
  guestCapacity: null,
  locations: {
    cities: [],
    venues: []
  },
  budget: {
    amount: null,
    currency: 'INR',
    notes: ''
  },
  proposal: {
    status: 'pending',
    details: {}
  },
  leadSource: '',
  entertainmentOptions: [],
  customerNotes: '',
  foodPreference: null,
  alcoholPreference: null,
  roomCount: null,
  residenceGuestCount: null,
  isCold: false,
  nextFollowUpTime: ''
};

const NewLeadModal = ({ isOpen, onClose, lead, onLeadAdded, initialTab = 'basic', initialCallData = null }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isLoading, setIsLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [stages, setStages] = useState([]);
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [proposalData, setProposalData] = useState(lead?.proposal || { status: 'pending', details: '' });
  const [counts, setCounts] = useState({
    commentCount: 0,
    taskCount: 0,
    callCount: 0
  });
  const toast = useToast();
  const originalData = useRef(null);

  const fetchCounts = async () => {
    if (!lead?.leadId) return;

    try {
      const [commentRes, taskRes, callRes] = await Promise.all([
        api.get(`/api/comments/lead/${lead.leadId}`),
        api.get(`/api/tasks/lead/${lead.leadId}`),
        api.get(`/api/call-logs/lead/${lead.leadId}`)
      ]);

      setCounts({
        commentCount: commentRes.data.total || 0,
        taskCount: taskRes.data.total || 0,
        callCount: callRes.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/api/users');
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          title: 'Error fetching users',
          description: error.response?.data?.message || error.message,
          status: 'error',
          duration: 5000,
        });
      }
    };

    const fetchConstants = async () => {
      try {
        const [eventTypesRes, leadSourcesRes, stagesRes, entertainmentOptionsRes] = await Promise.all([
          api.get('/api/constants/EVENT_TYPES'),
          api.get('/api/constants/LEAD_SOURCES'),
          api.get('/api/constants/STAGES'),
          api.get('/api/constants/ENTERTAINMENT_OPTIONS')
        ]);
        setEventTypes(eventTypesRes.data.values);
        setLeadSources(leadSourcesRes.data.values);
        setStages(stagesRes.data.values);
        setEntertainmentOptions(entertainmentOptionsRes.data.values);
        console.log('Fetched Event Types:', eventTypesRes.data.values);
        console.log('Fetched Lead Sources:', leadSourcesRes.data.values);
        console.log('Fetched Stages:', stagesRes.data.values);
      } catch (error) {
        console.error('Error fetching constants:', error);
      }
    };
    Promise.all([fetchConstants(), fetchUsers()]);
  }, []);

  useEffect(() => {
    if (lead) {
      console.log('Lead Data Received:', lead);
      console.log('Budget Amount:', lead.budget?.amount);
      fetchCounts();
      setFormData({
        ...lead,
        stage: lead.stage || { _id: 'stage_lead_in' },
        leadSource: lead.leadSource || null,
        locations: lead.locations || { cities: [], venues: [] },
        budget: lead.budget || { amount: null, currency: 'INR', notes: '' },
        proposal: lead.proposal || { status: 'pending', details: {} },
        events: lead.events.map(event => {
          console.log('Original Event Type:', event.type);
          const eventType = eventTypes.find(type => type.name === event.type);
          if (!eventType) {
            console.warn('No matching event type found for:', event.type);
          }
          const transformedEvent = {
            ...event,
            type: eventType ? eventType._id : event.type // Map to _id if found
          };
          console.log('Transformed Event:', transformedEvent);
          return transformedEvent;
        }) || [{
          type: '',
          guestCount: null,
          startTime: '',
          endTime: '',
          notes: ''
        }],
        eventDates: lead.eventDates || [],
        entertainmentOptions: lead.entertainmentOptions || []
      });
      originalData.current = JSON.parse(JSON.stringify(lead));
    } else {
      setFormData(initialFormData);
      originalData.current = null;
    }
  }, [lead, eventTypes]);

  const handleChange = (field, value) => {
    console.log('Field Change:', field, value);
    setFormData(prev => {
      let newValue = value;
      let updatedData = { ...prev };
      
      if (['roomCount', 'residenceGuestCount', 'guestCapacity'].includes(field)) {
        newValue = value === '' ? null : 
                  isNaN(Number(value)) ? prev[field] : Number(value);
      }
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedData[parent] = { ...prev[parent], [child]: newValue };
      } else {
        updatedData[field] = newValue;
      }

      console.log('Updated Form Data:', updatedData);
      return updatedData;
    });
  };

  const handleEventChange = (index, field, value) => {
    setFormData(prev => {
      const events = [...prev.events];
      let newValue = value;

      if (field === 'guestCount') {
        newValue = value === '' ? null : 
                  isNaN(Number(value)) ? events[index][field] : Number(value);
      }

      events[index] = {
        ...events[index],
        [field]: newValue
      };

      return {
        ...prev,
        events
      };
    });
  };

  const addEvent = () => {
    setFormData(prev => ({
      ...prev,
      events: [
        ...prev.events,
        {
          type: '',
          guestCount: null,
          startTime: '',
          endTime: '',
          notes: ''
        }
      ]
    }));
  };

  const removeEvent = (index) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Ensure stage is properly handled
      const stage = formData.stage;
      const stageId = typeof stage === 'object' ? 
                     (stage._id || stage.id || 'stage_lead_in') : 
                     (stage || 'stage_lead_in');

      const submitData = {
        ...formData,
        stage: stageId,
        leadSource: typeof formData.leadSource === 'object' ? 
                  (formData.leadSource?._id || formData.leadSource) : 
                  formData.leadSource,
        events: formData.events.map(event => {
          // If event.type is already an ID (string), use it directly
          if (typeof event.type === 'string' && !eventTypes.some(t => t.name === event.type)) {
            return event;
          }
          
          // Otherwise, try to find the event type by name and get its ID
          const eventType = event.type ? eventTypes.find(t => t.name === event.type) : null;
          return {
            ...event,
            type: eventType ? eventType._id : ''
          };
        })
      };

      console.log('Submitting lead data:', submitData);

      const response = lead
        ? await api.put(`/api/leads/${lead.leadId}`, submitData) 
        : await api.post('/api/leads', submitData);

      toast({
        title: lead ? 'Lead Updated' : 'Lead Created',
        description: lead ? 'Lead has been updated successfully' : 'New lead has been created',
        status: 'success',
        duration: 5000
      });

      onLeadAdded(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving lead:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="900px">
        <ModalHeader display="flex" alignItems="center" gap={4}>
          <Text>{lead ? 'Edit Lead' : 'Add New Lead'}</Text>
          {lead && (
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={async () => {
                if (window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
                  try {
                    await api.delete(`/api/leads/${lead.leadId}`);
                    toast({
                      title: 'Lead Deleted',
                      description: 'Lead has been deleted successfully',
                      status: 'success',
                      duration: 5000
                    });
                    onLeadAdded(); // Trigger refresh
                    onClose();
                  } catch (error) {
                    console.error('Error deleting lead:', error);
                    toast({
                      title: 'Error',
                      description: error.response?.data?.message || 'Failed to delete lead',
                      status: 'error',
                      duration: 5000
                    });
                  }
                }
              }}
            >
              Delete Lead
            </Button>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs>
            <TabList>
              <Tab>Basic Info</Tab>
              <Tab>Events</Tab>
              <Tab>Venue</Tab>
              <Tab>Budget</Tab>
              <Tab>Preferences</Tab>
              <Tab>Proposal</Tab>
              <Tab>
                Comments
                {counts.commentCount > 0 && (
                  <Badge ml={2} colorScheme="blue" borderRadius="full">
                    {counts.commentCount}
                  </Badge>
                )}
              </Tab>
              <Tab>
                Tasks
                {counts.taskCount > 0 && (
                  <Badge ml={2} colorScheme="purple" borderRadius="full">
                    {counts.taskCount}
                  </Badge>
                )}
              </Tab>
              <Tab>
                Calls
                {counts.callCount > 0 && (
                  <Badge ml={2} colorScheme="green" borderRadius="full">
                    {counts.callCount}
                  </Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <BasicInfoForm 
                    formData={formData} 
                    handleChange={handleChange}
                    stages={stages}
                    leadSources={leadSources}
                    users={users}
                  />
                </Grid>
              </TabPanel>

              <TabPanel>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <EventForm
                    events={formData.events}
                    eventTypes={eventTypes}
                    handleEventChange={handleEventChange}
                    removeEvent={removeEvent}
                  />
                  <Button onClick={addEvent} colorScheme="blue" size="sm">
                    Add Event
                  </Button>
                </Grid>
              </TabPanel>

              <TabPanel>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <VenueForm formData={formData} handleChange={handleChange} />
                </Grid>
              </TabPanel>

              <TabPanel>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <BudgetForm formData={formData} handleChange={handleChange} />
                </Grid>
              </TabPanel>

              <TabPanel>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <PreferencesForm 
                    formData={formData} 
                    handleChange={handleChange}
                    entertainmentOptions={entertainmentOptions}
                  />
                </Grid>
              </TabPanel>

              <TabPanel>
                {lead && lead.leadId ? (
                  <ProposalForm 
                    lead={formData}
                    onUpdate={(updatedLead) => {
                      setFormData(updatedLead);
                      toast({
                        title: 'Proposal updated',
                        status: 'success',
                        duration: 3000,
                      });
                    }}
                  />
                ) : (
                  <Box p={4} textAlign="center">
                    Save the lead first to update proposal
                  </Box>
                )}
              </TabPanel>

              <TabPanel>
                {lead && lead.leadId ? (
                  <CommentsForm leadId={lead.leadId} onUpdate={fetchCounts} />
                ) : (
                  <Box p={4} textAlign="center">
                    Save the lead first to add comments
                  </Box>
                )}
              </TabPanel>

              <TabPanel>
                {lead && lead.leadId ? (
                  <TasksForm leadId={lead.leadId} onUpdate={fetchCounts} />
                ) : (
                  <Box p={4} textAlign="center">
                    Save the lead first to add tasks
                  </Box>
                )}
              </TabPanel>

              <TabPanel>
                {lead && lead.leadId ? (
                  <CallsForm
              id="calls-form" leadId={lead.leadId} onUpdate={fetchCounts} />
                ) : (
                  <Box p={4} textAlign="center">
                    Save the lead first to add call logs
                  </Box>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {lead ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewLeadModal;
